import React from 'react';
import {
  Box,
  Typography,
  Menu,
  MenuItem,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FilterButton, StyledActionButton, StyledSegmentedControl, StyledSegmentButton } from './SectionBar.styled';

interface E55ComparisonFiltersProps {
  customFilters: {
    samplingRelevant?: string[];
    notSamplingRelevant?: string[];
    fromPeriod?: string;
    toPeriod?: string;
    direction?: string;
    onMSCChange?: (values: string[]) => void;
    onFromPeriodChange?: (period: string) => void;
    onToPeriodChange?: (period: string) => void;
    onDirectionChange?: (direction: string) => void;
    onExport?: (format: 'HTML' | 'PDF' | 'XLS') => void;
    isExporting?: boolean;
    yearQuarterOptions?: Array<{ value: string; label: string; year: string; quarter: string }>;
  };
}

const E55ComparisonFilters: React.FC<E55ComparisonFiltersProps> = ({ customFilters }) => {
  // State for dropdown menus
  const [mscValuesAnchorEl, setMscValuesAnchorEl] = React.useState<null | HTMLElement>(null);
  const [fromPeriodAnchorEl, setFromPeriodAnchorEl] = React.useState<null | HTMLElement>(null);
  const [toPeriodAnchorEl, setToPeriodAnchorEl] = React.useState<null | HTMLElement>(null);

  const isMscValuesMenuOpen = Boolean(mscValuesAnchorEl);
  const isFromPeriodMenuOpen = Boolean(fromPeriodAnchorEl);
  const isToPeriodMenuOpen = Boolean(toPeriodAnchorEl);

  // Generate year-quarter options for E55Comparison (from 2008 to current year)
  // Use cascaded options from parent if provided, otherwise generate all options
  const yearQuarterOptions = React.useMemo(() => {
    // If parent provides cascaded options, use them
    if (customFilters.yearQuarterOptions && customFilters.yearQuarterOptions.length > 0) {
      return customFilters.yearQuarterOptions;
    }
    
    // Otherwise, generate all options
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year >= 2008; year--) { // Reverse order to show recent years first
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({
          value,
          label,
          year: year.toString(),
          quarter
        });
      });
    }
    
    return options;
  }, [customFilters.yearQuarterOptions]);

  // MSC options
  const samplingRelevantOptions = [
    { id: 'UA', label: 'UA', category: 'Sampling relevant' },
    { id: 'UE', label: 'UE', category: 'Sampling relevant' },
    { id: 'UL', label: 'UL', category: 'Sampling relevant' },
    { id: 'UN', label: 'UN', category: 'Sampling relevant' },
    { id: 'UY', label: 'UY', category: 'Sampling relevant' },
  ];

  const notSamplingRelevantOptions = [
    { id: 'UI', label: 'UI', category: 'Not sampling relevant' },
    { id: 'UM', label: 'UM', category: 'Not sampling relevant' },
    { id: 'UP', label: 'UP', category: 'Not sampling relevant' },
    { id: 'UR', label: 'UR', category: 'Not sampling relevant' },
    { id: 'UX', label: 'UX', category: 'Not sampling relevant' },
    { id: 'UZ', label: 'UZ', category: 'Not sampling relevant' },
  ];

  const allMscOptions = [...samplingRelevantOptions, ...notSamplingRelevantOptions];

  const directionOptions = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  // Handlers
  const handleMscValuesClick = (event: React.MouseEvent<HTMLElement>) => {
    setMscValuesAnchorEl(event.currentTarget);
  };

  const handleMscValuesMenuClose = () => {
    setMscValuesAnchorEl(null);
  };

  const handleFromPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setFromPeriodAnchorEl(event.currentTarget);
  };

  const handleFromPeriodMenuClose = () => {
    setFromPeriodAnchorEl(null);
  };

  const handleFromPeriodChange = (period: string) => {
    customFilters.onFromPeriodChange?.(period);
    handleFromPeriodMenuClose();
  };

  const handleToPeriodClick = (event: React.MouseEvent<HTMLElement>) => {
    setToPeriodAnchorEl(event.currentTarget);
  };

  const handleToPeriodMenuClose = () => {
    setToPeriodAnchorEl(null);
  };

  const handleToPeriodChange = (period: string) => {
    customFilters.onToPeriodChange?.(period);
    handleToPeriodMenuClose();
  };

  const handleMscToggle = (value: string) => {
    const samplingRelevantCodes = ['UA', 'UE', 'UL', 'UN', 'UY'];
    const notSamplingRelevantCodes = ['UI', 'UM', 'UP', 'UR', 'UX', 'UZ'];
    
    const allCurrentValues = [
      ...(customFilters.samplingRelevant || []),
      ...(customFilters.notSamplingRelevant || [])
    ];
    
    const newAllValues = allCurrentValues.includes(value)
      ? allCurrentValues.filter(v => v !== value)
      : [...allCurrentValues, value];
    
    customFilters.onMSCChange?.(newAllValues);
  };

  const handleDirectionChange = (direction: string) => {
    customFilters.onDirectionChange?.(direction);
  };

  // Display labels
  const getFromPeriodLabel = () => {
    if (!customFilters.fromPeriod) return 'Select From';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.fromPeriod);
    return option ? option.label : 'Select From';
  };

  const getToPeriodLabel = () => {
    if (!customFilters.toPeriod) return 'Select To';
    const option = yearQuarterOptions.find(opt => opt.value === customFilters.toPeriod);
    return option ? option.label : 'Select To';
  };

  const getMscValuesLabel = () => {
    const samplingSelected = customFilters.samplingRelevant || [];
    const notSamplingSelected = customFilters.notSamplingRelevant || [];
    const totalSelected = samplingSelected.length + notSamplingSelected.length;
    
    if (totalSelected === 0) return 'Select MSCs';
    if (totalSelected === allMscOptions.length) return 'All MSCs';
    return `${totalSelected} MSCs`;
  };

  const getDirectionLabel = () => {
    return customFilters.direction || 'Inbound';
  };

  return (
    <>
      {/* Direction Filter */}
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
        <StyledSegmentedControl>
          {directionOptions.map((option) => (
            <StyledSegmentButton
              key={option.id}
              active={customFilters.direction === option.id}
              onClick={() => handleDirectionChange(option.id)}
              sx={{ 
                color: customFilters.direction === option.id ? '#ffffff' : '#00B6BA',
                minWidth: '70px'
              }}
            >
              {option.label}
            </StyledSegmentButton>
          ))}
        </StyledSegmentedControl>
      </Box>

      {/* From Period Filter */}
      <FilterButton onClick={handleFromPeriodClick}>
        <span className='label'>From</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getFromPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={fromPeriodAnchorEl}
        open={isFromPeriodMenuOpen}
        onClose={handleFromPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleFromPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleFromPeriodChange(option.value)}
            selected={customFilters.fromPeriod === option.value}
            sx={{
              fontWeight: customFilters.fromPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* To Period Filter */}
      <FilterButton onClick={handleToPeriodClick}>
        <span className='label'>To</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getToPeriodLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={toPeriodAnchorEl}
        open={isToPeriodMenuOpen}
        onClose={handleToPeriodMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '150px',
          },
        }}
      >
        <MenuItem onClick={() => handleToPeriodChange('')}>
          <Typography sx={{ fontStyle: 'italic', color: '#666' }}>Clear</Typography>
        </MenuItem>
        {yearQuarterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleToPeriodChange(option.value)}
            selected={customFilters.toPeriod === option.value}
            sx={{
              fontWeight: customFilters.toPeriod === option.value ? 700 : 400,
              fontSize: '14px',
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* MSC Values Filter */}
      <FilterButton onClick={handleMscValuesClick}>
        <span className='label'>MSCs</span>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {getMscValuesLabel()}
          </Typography>
          <FontAwesomeIcon icon={faChevronDown} size='sm' />
        </Box>
      </FilterButton>

      <Menu
        anchorEl={mscValuesAnchorEl}
        open={isMscValuesMenuOpen}
        onClose={handleMscValuesMenuClose}
        PaperProps={{
          style: {
            maxHeight: 300,
            width: '250px',
          },
        }}
      >
        {/* Sampling relevant section */}
        <MenuItem disabled sx={{ fontWeight: 700, color: '#00ACE7', fontSize: '12px' }}>
          Sampling relevant:
        </MenuItem>
        {samplingRelevantOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleMscToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px 4px 32px', // Indent for subcategory
            }}
          >
            <Checkbox
              checked={customFilters.samplingRelevant?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
        
        {/* Not sampling relevant section */}
        <MenuItem disabled sx={{ fontWeight: 700, color: '#FF6B35', fontSize: '12px', mt: 1 }}>
          Not sampling relevant:
        </MenuItem>
        {notSamplingRelevantOptions.map((option) => (
          <MenuItem
            key={option.id}
            onClick={() => handleMscToggle(option.id)}
            sx={{
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              padding: '4px 16px 4px 32px', // Indent for subcategory
            }}
          >
            <Checkbox
              checked={customFilters.notSamplingRelevant?.includes(option.id) || false}
              sx={{ mr: 1, padding: '4px' }}
            />
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Export Buttons */}
      <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('HTML')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'HTML'}
          </Typography>
        </StyledActionButton>
        <StyledActionButton
          onClick={() => customFilters.onExport?.('XLS')}
          disabled={customFilters.isExporting}
          sx={{
            backgroundColor: '#00ACE7',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#0095C7',
            },
            '&:disabled': {
              backgroundColor: '#B8C4CE',
              color: '#6C757D',
            }
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase' }}>
            {customFilters.isExporting ? <CircularProgress size={16} color="inherit" /> : 'XLS'}
          </Typography>
        </StyledActionButton>
      </Box>
    </>
  );
};

export default E55ComparisonFilters; 